import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Booking } from '../../pages/rentals/Bookings/types';

/**
 * Why the last list fetch failed, kept serialisable for the store. `status` is
 * absent when no response arrived at all (timeout, dropped connection, or an
 * API Gateway 502/504 whose missing CORS headers hide the status from the
 * browser); `message` is the server's own explanation when it sent one.
 */
export interface BookingsLoadError {
    status: number | null;
    message: string | null;
}

interface BookingPageState {
    bookings: Booking[];
    loading: boolean;
    error: BookingsLoadError | null;
    totalRows: number;
    page: number;
    rowsPerPage: number;
    breadcrumbPathAndBookingNumber: {
        path: string;
        bookingNumber: string;
    };
}

const initialState: BookingPageState = {
    bookings: [],
    loading: false,
    error: null,
    totalRows: 0,
    page: 0,
    rowsPerPage: 10,
    breadcrumbPathAndBookingNumber: {
        path: '',
        bookingNumber: '',
    },
};

const bookingPageSlice = createSlice({
    name: 'bookingPage',
    initialState,
    reducers: {
        initiateFetchBookings: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchBookingsSuccess: (state, action: PayloadAction<{ bookings: Booking[], totalRows: number }>) => {
            state.loading = false;
            state.bookings = action.payload.bookings;
            state.totalRows = action.payload.totalRows;
            state.error = null;
        },
        fetchBookingsFailure: (state, action: PayloadAction<BookingsLoadError>) => {
            state.loading = false;
            state.error = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setRowsPerPage: (state, action: PayloadAction<number>) => {
            state.rowsPerPage = action.payload;
            state.page = 0;
        },
        setBreadcrumbPathAndBookingNumber: (state, action: PayloadAction<{ path: string, bookingNumber: string }>) => {
            const data = {
                path: action.payload.path,
                bookingNumber: action.payload.bookingNumber,
            };
            state.breadcrumbPathAndBookingNumber = data;
        },
    },
});

export const { initiateFetchBookings, fetchBookingsSuccess, fetchBookingsFailure, setPage, setRowsPerPage, setBreadcrumbPathAndBookingNumber } =
    bookingPageSlice.actions;

export default bookingPageSlice.reducer;
